import React, { useState, useCallback, useEffect } from 'react';
import { isSafari } from 'react-device-detect';
import {
  AiFillExclamationCircle,
  AiOutlineMinus,
  AiOutlinePlus,
} from 'react-icons/ai';
import { InputNoForm } from '~/components/Forms';
import { RadioNoForm } from '~/components/Forms/RadioNoForm';
import Load from '~/components/Load';
import WhiteBox from '~/components/WhiteBox';
import { BoxHeader } from '~/pages/Usuario/styles';
import { showModalMessage } from '~/services/notification';
import {
  ActionInputMulti,
  ContainerMultiInputActions,
  GridContainer,
  Wrap,
} from '~/styles/components';
import ApiClient from '~/utils/ApiClient';
import ResponsaveisInfoIcon from '../ResponsaveisInfoIcon';

// campos explicitamente vazios: usar {} deixaria value === undefined, o React
// pararia de controlar os inputs e o texto do CNPJ anterior ficaria na tela
// Exportado porque o estado inicial e o handleAddBox precisam usar a mesma
// forma: qualquer campo ausente vira value === undefined e o React (ou o
// RadioGroup do MUI) passa a tratar o campo como nao-controlado.
export const RESPONSAVEL_VAZIO = {
  uuid: '',
  cpf: '',
  name: '',
  email: '',
  phone_mobile: '',
  rg: '',
  birth_date: '',
  cargo: '',
  corporate_email: '',
  send_email: '',
  user_exists: false,
  representante_exists: false,
  campos_bloqueados: [],
};

const CAMPOS_RESPONSAVEL = [
  'cpf',
  'name',
  'email',
  'phone_mobile',
  'rg',
  'birth_date',
  'cargo',
  'corporate_email',
  'send_email',
];

// send_email e tinyInteger nullable sem default nem backfill (migration
// 2022_04_20_152117), entao representante anterior a abr/2022 vem com null.
// Number(null) daria 0 e a tela mostraria "Nao" travado, sem o usuario poder
// definir - nunca respondido nao e o mesmo que ter respondido que nao.
const normalizarSendEmail = valor =>
  valor == null ? '' : String(Number(valor));

// Fotografa quais campos ja vieram preenchidos da API. Campo que veio vazio
// continua editavel para o usuario completar - representante com cadastro
// incompleto (sem celular ou sem nascimento) seria barrado pela validacao de
// envio sem poder corrigir, o mesmo beco sem saida do bug original.
const bloquearCamposPreenchidos = valores =>
  CAMPOS_RESPONSAVEL.filter(campo => valores[campo] !== '' && valores[campo] != null);

// Consulta a fotografia, nunca o valor atual: olhar o valor travaria o campo
// na primeira tecla que o proprio usuario digitasse.
const travado = (inputField, campo) =>
  Boolean(inputField.campos_bloqueados?.includes(campo));

export const ResponsavelLegal = ({
  pjUuid,
  viewMode,
  responsaveisLegal,
  setResponsaveisLegal,
}) => {
  const [loading, setLoading] = useState(false);
  const [responsaveisLegalCnpj, setResponsaveisLegalCnpj] = useState([]);

  const searchResponsaveisLegalByCNPJ = useCallback(async () => {
    try {
      setResponsaveisLegalCnpj([]);

      if (!pjUuid) {
        setResponsaveisLegal(prevState =>
          prevState.some(item => item?.representante_exists)
            ? [{ ...RESPONSAVEL_VAZIO }]
            : prevState,
        );
        return;
      }

      setLoading(true);

      const resp = await ApiClient.doRequest({
        uri: `/find/branch-representantes/${pjUuid}`,
        method: ApiClient.GET,
        expectedStatus: 200,
      });

      const representantes = resp.data.data;

      setResponsaveisLegalCnpj(representantes);

      setResponsaveisLegal(prevState => {
        // produtora com representante(s): dados vem do vinculo com o CNPJ,
        // preenchidos e nao editaveis
        if (representantes?.length) {
          return representantes.map(representante => {
            const valores = {
              ...RESPONSAVEL_VAZIO,
              cpf: representante.cpf ?? '',
              name: representante.name ?? '',
              email: representante.email ?? '',
              phone_mobile: representante.user_phone_mobile ?? '',
              rg: representante.rg ?? '',
              birth_date: representante.birth_date ?? '',
              cargo: representante.cargo ?? '',
              corporate_email: representante.corporate_email ?? '',
              send_email: normalizarSendEmail(representante.send_email),
              user_exists: true,
              representante_exists: true,
            };

            return {
              ...valores,
              campos_bloqueados: bloquearCamposPreenchidos(valores),
            };
          });
        }

        // produtora sem representante: segue editavel para cadastro manual
        return prevState.some(item => item?.representante_exists)
          ? [{ ...RESPONSAVEL_VAZIO }]
          : prevState;
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      await showModalMessage({
        type: 'error',
        text: `Erro ao buscar Responsável(veis) Legal do CNPJ informado`,
      });
    }
  }, [pjUuid, setResponsaveisLegal]);

  useEffect(() => {
    searchResponsaveisLegalByCNPJ();
  }, [searchResponsaveisLegalByCNPJ]);

  const handleEmailExists = async (event, index) => {
    const { value } = event.target;

    if (!value) return;

    setLoading(true);

    try {
      await ApiClient.doRequest({
        uri: `user/exist-by-email/${value}`,
        method: ApiClient.GET,
        expectedStatus: 200,
      });

      setLoading(false);

      await showModalMessage({
        type: 'error',
        text: `Email '${value}' já cadastrado. Por favor, informe outro.`,
      });

      const values = [...responsaveisLegal];
      values[index][event.target.name] = '';
      setResponsaveisLegal(values);
    } catch (err) {
      setLoading(false);
    }
  };

  const handleSearchPhysicalPerson = async (event, index) => {
    const { value } = event.target;
    const searchValue = value?.replace(/[^0-9]/g, '');

    try {
      if (!searchValue || searchValue.length !== 11) throw new Error();

      setLoading(true);

      const response = await ApiClient.doRequest({
        uri: `/user/find/${searchValue}`,
        method: ApiClient.GET,
        expectedStatus: 200,
      });

      const data = response?.data?.data;

      let representante = null;

      if (pjUuid !== undefined) {
        const respRep = await ApiClient.doRequest({
          uri: `/find/representante?branch_uuid=${pjUuid}&user_uuid=${data?.uuid}`,
          method: ApiClient.GET,
          expectedStatus: 200,
        });

        if (typeof respRep?.data?.data?.uuid === 'string') {
          representante = respRep.data.data;
        }
      }

      const values = [...responsaveisLegal];

      // so o que veio da API entra na fotografia. Usar o estado atual travaria
      // o que o usuario digitou enquanto a requisicao estava em voo.
      const daApi = {
        name: data?.user_personal_data?.name ?? '',
        email: data?.email ?? '',
        phone_mobile: data?.user_personal_data?.phone_mobile ?? '',
        rg: data?.user_personal_data?.rg ?? '',
        birth_date: data?.user_birth_address?.birth_date ?? '',
        ...(representante
          ? {
              cargo: representante.cargo ?? '',
              corporate_email: representante.corporate_email ?? '',
              send_email: normalizarSendEmail(representante.send_email),
            }
          : {}),
      };

      values[index] = {
        ...values[index],
        ...daApi,
        user_exists: true,
        representante_exists: Boolean(representante),
        campos_bloqueados: bloquearCamposPreenchidos(daApi),
      };
      setResponsaveisLegal(values);

      setLoading(false);
    } catch (err) {
      const values = [...responsaveisLegal];
      values[index] = {
        ...values[index],
        uuid: '',
        name: '',
        corporate_email: '',
        email: '',
        phone_mobile: '',
        rg: '',
        birth_date: '',
        cargo: '',
        send_email: '',
        user_exists: false,
        representante_exists: false,
        campos_bloqueados: [],
      };
      setResponsaveisLegal(values);

      setLoading(false);
    }
  };

  const handleRemoveBox = ({ index }) => {
    setResponsaveisLegal(responsaveisLegal?.filter((_, idx) => idx !== index));
  };

  const handleAddBox = () => {
    setResponsaveisLegal(prevState => [...prevState, { ...RESPONSAVEL_VAZIO }]);
  };

  const handleChange = ({ index, event }) => {
    const values = [...responsaveisLegal];
    values[index][event.target.name] = event.target.value;
    setResponsaveisLegal(values);
  };

  return (
    <>
      <Load inLoad={loading} />

      <WhiteBox column marginBottom="30px">
        <BoxHeader style={{ justifyContent: 'space-between' }}>
          <span className="boxText">Responsável Legal:</span>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <ResponsaveisInfoIcon
              responsaveisExistentes={responsaveisLegalCnpj}
            />
            <span tooltip="Preencha os dados" style={{ marginRight: 5 }}>
              <AiFillExclamationCircle />
            </span>
          </div>
        </BoxHeader>

        {responsaveisLegal?.map((inputField, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Wrap key={`${inputField}~${index}`}>
            <WhiteBox column style={{ bottom: '0' }}>
              <BoxHeader style={{ justifyContent: 'flex-end' }}>
                {!viewMode && (
                  <ContainerMultiInputActions>
                    {responsaveisLegal.length !== 1 && (
                      <ActionInputMulti
                        type="button"
                        onClick={() =>
                          handleRemoveBox({
                            index,
                          })
                        }
                      >
                        <AiOutlineMinus color="#AAA" size={24} />
                      </ActionInputMulti>
                    )}

                    {responsaveisLegal.length - 1 === index && (
                      <ActionInputMulti type="button" onClick={handleAddBox}>
                        <AiOutlinePlus color="#AAA" size={24} />
                      </ActionInputMulti>
                    )}
                  </ContainerMultiInputActions>
                )}
              </BoxHeader>
              <Wrap>
                <GridContainer cols={3}>
                  <InputNoForm
                    type="text"
                    name="cpf"
                    mask="999.999.999-99"
                    maskChar=""
                    questionTooltip="Caso o CPF esteja cadastrado como Representante neste CNPJ, não há necessidade de um novo cadastro"
                    label="CPF"
                    value={inputField.cpf}
                    disabled={viewMode || travado(inputField, 'cpf')}
                    onChange={event =>
                      handleChange({
                        index,
                        event,
                      })
                    }
                    onBlur={event => handleSearchPhysicalPerson(event, index)}
                    required={Object.values(responsaveisLegalCnpj).length <= 0}
                  />
                  <InputNoForm
                    type="text"
                    name="name"
                    label="Nome Civil/Nome Social"
                    required={Object.values(responsaveisLegalCnpj).length <= 0}
                    value={inputField.name}
                    disabled={viewMode || travado(inputField, 'name')}
                    onChange={event =>
                      handleChange({
                        index,
                        event,
                      })
                    }
                  />
                  <InputNoForm
                    type="text"
                    name="email"
                    label="E-mail"
                    value={inputField.email}
                    disabled={viewMode || travado(inputField, 'email')}
                    onBlur={e => handleEmailExists(e, index)}
                    required={Object.values(responsaveisLegalCnpj).length <= 0}
                    onChange={event =>
                      handleChange({
                        index,
                        event,
                      })
                    }
                  />
                  <InputNoForm
                    type="text"
                    name="phone_mobile"
                    label="Celular"
                    mask="(99) 99999-9999"
                    maskChar=""
                    disabled={viewMode || travado(inputField, 'phone_mobile')}
                    value={inputField.phone_mobile}
                    required={Object.values(responsaveisLegalCnpj).length <= 0}
                    onChange={event =>
                      handleChange({
                        index,
                        event,
                      })
                    }
                  />
                  <InputNoForm
                    type="text"
                    name="rg"
                    label="RG/RNE"
                    disabled={viewMode || travado(inputField, 'rg')}
                    value={inputField.rg}
                    required={Object.values(responsaveisLegalCnpj).length <= 0}
                    onChange={event =>
                      handleChange({
                        index,
                        event,
                      })
                    }
                  />
                  <InputNoForm
                    type="date"
                    name="birth_date"
                    label="Data de nascimento"
                    disabled={viewMode || travado(inputField, 'birth_date')}
                    acceptPast
                    value={inputField.birth_date}
                    placeholder={isSafari && 'aaaa-mm-dd'}
                    required={Object.values(responsaveisLegalCnpj).length <= 0}
                    onChange={event =>
                      handleChange({
                        index,
                        event,
                      })
                    }
                  />
                  <InputNoForm
                    type="text"
                    name="cargo"
                    label="Cargo"
                    required={Object.values(responsaveisLegalCnpj).length <= 0}
                    value={inputField.cargo}
                    disabled={viewMode || travado(inputField, 'cargo')}
                    onChange={event =>
                      handleChange({
                        index,
                        event,
                      })
                    }
                  />
                  <InputNoForm
                    type="text"
                    name="corporate_email"
                    label="E-mail Corporativo"
                    value={inputField.corporate_email}
                    disabled={viewMode || travado(inputField, 'corporate_email')}
                    required={Object.values(responsaveisLegalCnpj).length <= 0}
                    onChange={event =>
                      handleChange({
                        index,
                        event,
                      })
                    }
                  />
                </GridContainer>
              </Wrap>
              {!viewMode && !loading && (
                <Wrap>
                  <RadioNoForm
                    name="send_email"
                    label="Deseja que esse usuário receba notificações automáticas enviadas pelo sistema?"
                    required
                    disabled={travado(inputField, 'send_email')}
                    value={inputField.send_email}
                    onChange={event => {
                      handleChange({
                        index,
                        event,
                      });
                    }}
                  />
                </Wrap>
              )}
            </WhiteBox>
          </Wrap>
        ))}
      </WhiteBox>
    </>
  );
};
